import React, {useCallback, useMemo, useState} from 'react';
import cn from 'classnames';

import {EEventSource} from 'coreClasses/events/IEvents';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import Skeleton from 'coreComponents/Skeleton';

import {VisibilityContextProvider} from 'brand/app/contexts/viewportProductContext';
import Carousel from 'brandComponents/Carousel';
import {EProductMediaType} from 'brandComponents/product/ProductMedia';
import ProductTile from 'brandComponents/ProductTile';
import useViewItemListEvent from 'brandHooks/useViewItemListEvent';

import {CAROUSEL_CONFIG, CAROUSEL_CONFIG_BIG_TILE} from './config';
import {IRecommendationsCarouselProps} from './IRecommendationsCarousel';

import styles from './RecommendationsCarousel.module.scss';

const RecommendationsCarousel = ({products, source, bigTile = false}: IRecommendationsCarouselProps) => {
	const [resetCarousel, setResetCarousel] = useState(false);
	const {handleProductVisibility} = useViewItemListEvent(products, source);
	const productsLength = products.length;
	const breakpointsMap = ViewportMgr.getBreakpointsMap();

	const getSlidesPerViewForCurrentBreakpoint = useCallback((breakpoints, currentBreakpointValue, slidesPerViewRoot): number => {
		const breakpointKeys = Object.keys(breakpoints)
			.map(Number)
			.sort((a, b) => b - a);

		for (const breakpoint of breakpointKeys) {
			if (currentBreakpointValue >= breakpoint && breakpoints[breakpoint]?.slidesPerView) {
				return breakpoints[breakpoint].slidesPerView as number;
			}
		}

		return slidesPerViewRoot;
	}, []);

	const carouselConfig = useMemo(() => {
		const isMobile = ViewportMgr.isMobile();
		const isCompleteTheSet = source === EEventSource.COMPLETE_THE_SET;
		const config = bigTile ? CAROUSEL_CONFIG_BIG_TILE : CAROUSEL_CONFIG;
		let currentSlidesPerView = config.slidesPerView as number;

		if (isCompleteTheSet && !isMobile) {
			return Object.assign({}, config, {navigationConfig: {enabled: false}});
		}

		if (config.breakpoints) {
			currentSlidesPerView = getSlidesPerViewForCurrentBreakpoint(
				config.breakpoints,
				breakpointsMap[ViewportMgr.getCurrentBreakpoint()],
				config.slidesPerView,
			);
		}

		const navigationEnabled = productsLength > currentSlidesPerView;

		return Object.assign({}, config, {
			navigationConfig: {
				...config.navigationConfig,
				enabled: navigationEnabled,
			},
		});
	}, [source, bigTile, productsLength]);

	const visibilityContextValue = useMemo(
		() => ({
			handleVisibilityChange: handleProductVisibility,
		}),
		[handleProductVisibility],
	);

	const slides = useMemo(
		() =>
			products.map((recommendation, index) => {
				return {
					key: `${recommendation.getProductId()}-${index}`,
					elem: recommendation.isReady() ? (
						<VisibilityContextProvider value={visibilityContextValue}>
							<div className={styles['recommendation-carousel__product']} onClick={() => setResetCarousel((prev) => !prev)}>
								<ProductTile
									productId={recommendation.getProductId()}
									showColorSwatch
									customProductMediaType={EProductMediaType.PICTURE}
									sourceOverwritten={EEventSource.YOU_MAY_ALSO_LIKE}
								/>
							</div>
						</VisibilityContextProvider>
					) : (
						<Skeleton classNames={styles['recommendation-carousel__product__loading']} />
					),
				};
			}),
		[products],
	);

	return (
		<div className={cn(styles['recommendation-carousel'], 'recommendation-carousel')}>
			<Carousel
				key={resetCarousel ? 'reset' : 'normal'}
				config={carouselConfig}
				classNames={{
					swiper: styles['recommendation-carousel__swiper'],
					slide: styles['recommendation-carousel__slide'],
					navigation: cn('recommendation-carousel__navigation', styles['recommendation-carousel__navigation'], {
						[styles['recommendation-carousel__navigation--big-tile']]: bigTile,
					}),
				}}
			>
				{slides}
			</Carousel>
		</div>
	);
};

export default RecommendationsCarousel;
